import React, { useState } from "react";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 640);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

// ─── Milestone definitions ───────────────────────────────────────────────────
const MILESTONES = [
  // First steps
  { id: "first_payment", icon: "💳", title: "First Step", desc: "Logged your first payment", category: "journey" },
  { id: "first_account", icon: "📋", title: "Debt Tracker", desc: "Added your first account", category: "journey" },
  // Payment streaks
  { id: "streak_2",  icon: "🔥", title: "On a Roll",      desc: "2-month payment streak",  category: "streak", streak: 2  },
  { id: "streak_3",  icon: "🔥", title: "Heating Up",     desc: "3-month payment streak",  category: "streak", streak: 3  },
  { id: "streak_6",  icon: "🔥", title: "On Fire",        desc: "6-month payment streak",  category: "streak", streak: 6  },
  { id: "streak_12", icon: "🏆", title: "Unstoppable",    desc: "12-month payment streak", category: "streak", streak: 12 },
  // Total paid milestones
  { id: "paid_500",   icon: "⭐", title: "Getting Started", desc: "Paid off $500 total",    category: "paid", amount: 500   },
  { id: "paid_1000",  icon: "⭐", title: "Four Figures",    desc: "Paid off $1,000 total",  category: "paid", amount: 1000  },
  { id: "paid_2500",  icon: "⭐", title: "Momentum",        desc: "Paid off $2,500 total",  category: "paid", amount: 2500  },
  { id: "paid_5000",  icon: "💎", title: "Halfway Hero",    desc: "Paid off $5,000 total",  category: "paid", amount: 5000  },
  { id: "paid_10000", icon: "💎", title: "Five Figures",    desc: "Paid off $10,000 total", category: "paid", amount: 10000 },
  { id: "paid_25000", icon: "👑", title: "Debt Slayer",     desc: "Paid off $25,000 total", category: "paid", amount: 25000 },
  // Progress milestones
  { id: "progress_25", icon: "🌱", title: "Quarter Down",  desc: "25% paid off on any account",  category: "progress", pct: 25 },
  { id: "progress_50", icon: "🌿", title: "Halfway There", desc: "50% paid off on any account",  category: "progress", pct: 50 },
  { id: "progress_75", icon: "🌳", title: "Almost There",  desc: "75% paid off on any account",  category: "progress", pct: 75 },
  // Account payoff
  { id: "account_paid_1", icon: "🎉", title: "Debt Free!",       desc: "Paid off your first account",  category: "payoff", count: 1 },
  { id: "account_paid_2", icon: "🎊", title: "Two Down",         desc: "Paid off 2 accounts",          category: "payoff", count: 2 },
  { id: "account_paid_3", icon: "🏅", title: "Hat Trick",        desc: "Paid off 3 accounts",          category: "payoff", count: 3 },
  // Interest saved
  { id: "interest_saved_50",   icon: "💰", title: "First Savings",    desc: "Saved $50 in interest by paying extra",    category: "savings", saved: 50    },
  { id: "interest_saved_100",  icon: "💰", title: "Smart Saver",      desc: "Saved $100 in interest by paying extra",   category: "savings", saved: 100   },
  { id: "interest_saved_250",  icon: "💰", title: "Interest Fighter",  desc: "Saved $250 in interest by paying extra",   category: "savings", saved: 250   },
  { id: "interest_saved_500",  icon: "💰", title: "Interest Buster",   desc: "Saved $500 in interest by paying extra",   category: "savings", saved: 500   },
  { id: "interest_saved_1000", icon: "💎", title: "Money Wizard",      desc: "Saved $1,000 in interest by paying extra", category: "savings", saved: 1000  },
  { id: "interest_saved_2500", icon: "💎", title: "Interest Slayer",   desc: "Saved $2,500 in interest by paying extra", category: "savings", saved: 2500  },
  { id: "interest_saved_5000", icon: "👑", title: "Freedom Fighter",   desc: "Saved $5,000 in interest by paying extra", category: "savings", saved: 5000  },

  // 💸 Extra Payments
  { id: "extra_first",    icon: "⚡", title: "Above & Beyond",   desc: "Made your first extra payment above the minimum", category: "extra" },
  { id: "extra_count_5",  icon: "⚡", title: "Extra Effort",     desc: "Made 5 extra payments above the minimum",         category: "extra", count: 5  },
  { id: "extra_count_10", icon: "⚡", title: "Consistent Pusher",desc: "Made 10 extra payments above the minimum",        category: "extra", count: 10 },
  { id: "extra_count_25", icon: "⚡", title: "Extra Mile",       desc: "Made 25 extra payments above the minimum",       category: "extra", count: 25 },
  { id: "extra_month_100",  icon: "💥", title: "Power Month",    desc: "Put $100+ extra toward principal in one month",  category: "extra", monthExtra: 100  },
  { id: "extra_month_500",  icon: "💥", title: "Power Surge",    desc: "Put $500+ extra toward principal in one month",  category: "extra", monthExtra: 500  },
  { id: "extra_month_1000", icon: "💥", title: "Mega Month",     desc: "Put $1,000+ extra toward principal in one month",category: "extra", monthExtra: 1000 },

  // 📊 Debt Reduction
  { id: "debt_reduced_10", icon: "📉", title: "Chipping Away",   desc: "Reduced your total debt by 10%",  category: "reduction", pct: 10 },
  { id: "debt_reduced_25", icon: "📉", title: "Quarter Gone",    desc: "Reduced your total debt by 25%",  category: "reduction", pct: 25 },
  { id: "debt_reduced_50", icon: "📉", title: "Halfway Free",    desc: "Reduced your total debt by 50%",  category: "reduction", pct: 50 },
  { id: "interest_cost_50",  icon: "📊", title: "Interest Down", desc: "Lowered your monthly interest cost by $50",  category: "reduction", interestDrop: 50  },
  { id: "interest_cost_100", icon: "📊", title: "Interest Tamed",desc: "Lowered your monthly interest cost by $100", category: "reduction", interestDrop: 100 },
  { id: "interest_cost_200", icon: "📊", title: "Interest Crushed",desc: "Lowered your monthly interest cost by $200",category: "reduction", interestDrop: 200 },
  { id: "principal_beats_interest", icon: "⚖️", title: "Principal Winner", desc: "Paid more principal than interest in a single payment (non-credit card)", category: "reduction" },

  // 🎯 Strategy
  { id: "avalanche_win",  icon: "🏔", title: "Avalanche Champion", desc: "Paid off your highest-interest debt first",       category: "strategy" },
  { id: "snowball_win",   icon: "⛄", title: "Snowball Champion",  desc: "Paid off your smallest balance debt first",        category: "strategy" },
  { id: "rolled_forward", icon: "🔄", title: "Roll It Forward",    desc: "Freed up a minimum payment and rolled it forward", category: "strategy" },

  // 🏁 End Game
  { id: "under_10k", icon: "🏁", title: "Under $10K",      desc: "Total debt remaining under $10,000", category: "endgame", under: 10000 },
  { id: "under_5k",  icon: "🏁", title: "Almost There",    desc: "Total debt remaining under $5,000",  category: "endgame", under: 5000  },
  { id: "under_1k",  icon: "🚀", title: "Final Stretch",   desc: "Total debt remaining under $1,000",  category: "endgame", under: 1000  },
  { id: "dfdate_1yr",icon: "📅", title: "One Year Away",   desc: "Debt-free date is within one year",  category: "endgame" },
  { id: "final_payment", icon: "🎊", title: "DEBT FREE!",  desc: "Logged your final payment — you did it!", category: "endgame" },
];

// ─── Reward calculation ───────────────────────────────────────────────────────
export function calculateRewards(accounts, allPayments, monthlyPaymentsHistory = []) {
  const totalPaid = Object.values(allPayments).flat().reduce((s, p) => s + (p.amount || 0), 0);
  const totalPrincipal = Object.values(allPayments).flat().reduce((s, p) => s + (p.principal || 0), 0);

  // Calculate streak from monthlyPaymentsHistory
  const streak = calculateStreak(monthlyPaymentsHistory, accounts);

  // Calculate max progress on any account
  const maxProgress = accounts.reduce((best, acct) => {
    const payments = allPayments[acct.id] || [];
    const principalPaid = payments.reduce((s, p) => s + (p.principal || 0), 0);
    const original = acct.balance + principalPaid;
    if (original <= 0) return best;
    return Math.max(best, (principalPaid / original) * 100);
  }, 0);

  // Count fully paid accounts (balance <= 0 or >95% principal paid)
  const paidAccounts = accounts.filter(acct => {
    const payments = allPayments[acct.id] || [];
    const principalPaid = payments.reduce((s, p) => s + (p.principal || 0), 0);
    const original = acct.balance + principalPaid;
    return acct.balance <= 0 || (original > 0 && principalPaid / original >= 0.98);
  }).length;

  // Calculate interest saved: sum of extra payments made (principal above minimum)
  // For each account, compare actual principal paid vs what min payments would have paid
  const interestSaved = accounts.reduce((total, acct) => {
    const payments = allPayments[acct.id] || [];
    if (!payments.length || !acct.minPayment || !acct.rate) return total;

    const r = acct.rate / 100 / 12;
    let saved = 0;

    payments.forEach(p => {
      const extra = Math.max((p.amount || 0) - acct.minPayment, 0);
      if (extra <= 0) return;
      // Estimate interest saved: extra principal × remaining months of interest
      // Simple approximation: extra × rate × avg remaining months
      const remainingMonths = acct.balance > 0 && acct.minPayment > acct.balance * r
        ? Math.ceil(Math.log(acct.minPayment / (acct.minPayment - acct.balance * r)) / Math.log(1 + r))
        : 12;
      saved += extra * r * (remainingMonths / 2);
    });

    return total + saved;
  }, 0);

  const hasAnyPayment = Object.values(allPayments).flat().length > 0;
  const hasAccount = accounts.length > 0;
  const allPaymentsList = Object.values(allPayments).flat();

  // Extra payment metrics
  const extraPaymentsAboveMin = accounts.reduce((count, acct) => {
    const payments = allPayments[acct.id] || [];
    return count + payments.filter(p => (p.amount || 0) > (acct.minPayment || 0)).length;
  }, 0);

  // Max extra principal in a single month across all accounts
  const monthlyExtraMap = {};
  accounts.forEach(acct => {
    const payments = allPayments[acct.id] || [];
    payments.forEach(p => {
      const extra = Math.max((p.amount || 0) - (acct.minPayment || 0), 0);
      if (extra <= 0) return;
      const key = p.date ? p.date.slice(0, 7) : "unknown";
      monthlyExtraMap[key] = (monthlyExtraMap[key] || 0) + extra;
    });
  });
  const maxMonthExtra = Math.max(0, ...Object.values(monthlyExtraMap));

  // Debt reduction — compare original total debt to current
  const totalCurrentDebt = accounts.reduce((s, a) => s + a.balance, 0);
  const totalOriginalDebt = accounts.reduce((s, acct) => {
    const payments = allPayments[acct.id] || [];
    const principalPaid = payments.reduce((s2, p) => s2 + (p.principal || 0), 0);
    return s + acct.balance + principalPaid;
  }, 0);
  const debtReductionPct = totalOriginalDebt > 0 ? ((totalOriginalDebt - totalCurrentDebt) / totalOriginalDebt) * 100 : 0;

  // Monthly interest cost drop
  const originalMonthlyInterest = accounts.reduce((s, acct) => {
    const payments = allPayments[acct.id] || [];
    const principalPaid = payments.reduce((s2, p) => s2 + (p.principal || 0), 0);
    const original = acct.balance + principalPaid;
    return s + (original * (acct.rate / 100)) / 12;
  }, 0);
  const currentMonthlyInterest = accounts.reduce((s, a) => s + (a.balance * (a.rate / 100)) / 12, 0);
  const interestDrop = Math.max(0, originalMonthlyInterest - currentMonthlyInterest);

  // Principal beats interest (non-credit card accounts)
  const principalBeatsInterest = allPaymentsList.some(p => {
    const acct = accounts.find(a => allPayments[a.id]?.includes(p));
    if (acct && acct.type === "Credit Card") return false;
    return (p.principal || 0) > (p.interest || 0) && (p.principal || 0) > 0;
  });

  // Strategy wins — check which account was paid off first
  const paidOffAccounts = accounts.filter(acct => {
    const payments = allPayments[acct.id] || [];
    const principalPaid = payments.reduce((s, p) => s + (p.principal || 0), 0);
    const original = acct.balance + principalPaid;
    return acct.balance <= 0 || (original > 0 && principalPaid / original >= 0.98);
  });
  const highestRateFirst = paidOffAccounts.length > 0 && accounts.length > 1 && (() => {
    const maxRate = Math.max(...accounts.map(a => a.rate));
    return paidOffAccounts.some(a => a.rate === maxRate);
  })();
  const smallestBalanceFirst = paidOffAccounts.length > 0 && accounts.length > 1 && (() => {
    const allPaymentsList2 = accounts.map(acct => {
      const payments = allPayments[acct.id] || [];
      const principalPaid = payments.reduce((s, p) => s + (p.principal || 0), 0);
      return { ...acct, original: acct.balance + principalPaid };
    });
    const minOriginal = Math.min(...allPaymentsList2.map(a => a.original));
    return paidOffAccounts.some(a => {
      const orig = allPaymentsList2.find(x => x.id === a.id)?.original || 0;
      return orig === minOriginal;
    });
  })();

  // Rolled forward: any account where extra payment matches a freed minimum
  const rolledForward = accounts.length > 1 && paidOffAccounts.length > 0 && extraPaymentsAboveMin > 0;

  // Debt-free date within 1 year
  const debtFreeWithinYear = accounts.length > 0 && accounts.every(acct => {
    if (acct.balance <= 0) return true;
    const r = acct.rate / 100 / 12;
    if (acct.minPayment <= acct.balance * r) return false;
    const months = Math.ceil(Math.log(acct.minPayment / (acct.minPayment - acct.balance * r)) / Math.log(1 + r));
    return months <= 12;
  });

  // Final payment: any account at 0 balance
  const finalPaymentLogged = accounts.some(a => a.balance <= 0) && hasAnyPayment;

  const earned = MILESTONES.filter(m => {
    if (m.id === "first_payment") return hasAnyPayment;
    if (m.id === "first_account") return hasAccount;
    if (m.category === "streak") return streak.current >= m.streak;
    if (m.category === "paid") return totalPaid >= m.amount;
    if (m.category === "progress") return maxProgress >= m.pct;
    if (m.category === "payoff") return paidAccounts >= m.count;
    if (m.category === "savings") return interestSaved >= m.saved;
    // Extra payments
    if (m.id === "extra_first") return extraPaymentsAboveMin >= 1;
    if (m.id === "extra_count_5")  return extraPaymentsAboveMin >= 5;
    if (m.id === "extra_count_10") return extraPaymentsAboveMin >= 10;
    if (m.id === "extra_count_25") return extraPaymentsAboveMin >= 25;
    if (m.id === "extra_month_100")  return maxMonthExtra >= 100;
    if (m.id === "extra_month_500")  return maxMonthExtra >= 500;
    if (m.id === "extra_month_1000") return maxMonthExtra >= 1000;
    // Debt reduction
    if (m.id === "debt_reduced_10") return debtReductionPct >= 10;
    if (m.id === "debt_reduced_25") return debtReductionPct >= 25;
    if (m.id === "debt_reduced_50") return debtReductionPct >= 50;
    if (m.id === "interest_cost_50")  return interestDrop >= 50;
    if (m.id === "interest_cost_100") return interestDrop >= 100;
    if (m.id === "interest_cost_200") return interestDrop >= 200;
    if (m.id === "principal_beats_interest") return principalBeatsInterest;
    // Strategy
    if (m.id === "avalanche_win")  return highestRateFirst;
    if (m.id === "snowball_win")   return smallestBalanceFirst;
    if (m.id === "rolled_forward") return rolledForward;
    // End game — require at least one payment so adding a small loan doesn't trigger these
    if (m.id === "under_10k") return hasAnyPayment && totalCurrentDebt > 0 && totalCurrentDebt < 10000;
    if (m.id === "under_5k")  return hasAnyPayment && totalCurrentDebt > 0 && totalCurrentDebt < 5000;
    if (m.id === "under_1k")  return hasAnyPayment && totalCurrentDebt > 0 && totalCurrentDebt < 1000;
    if (m.id === "dfdate_1yr") return hasAnyPayment && debtFreeWithinYear;
    if (m.id === "final_payment") return finalPaymentLogged;
    return false;
  }).map(m => m.id);

  return { earned, streak, totalPaid, totalPrincipal, maxProgress, paidAccounts, interestSaved };
}

function calculateStreak(history, accounts) {
  if (!history || history.length === 0) return { current: 0, longest: 0 };
  const sorted = [...history].sort((a, b) => b.localeCompare(a));
  let current = 0;
  let longest = 0;
  let prev = null;
  for (const monthKey of sorted) {
    if (!prev) { current = 1; prev = monthKey; continue; }
    const [py, pm] = prev.split("-").map(Number);
    const [cy, cm] = monthKey.split("-").map(Number);
    const diff = (py * 12 + pm) - (cy * 12 + cm);
    if (diff === 1) { current++; longest = Math.max(longest, current); prev = monthKey; }
    else break;
  }
  longest = Math.max(longest, current);
  return { current, longest };
}

// ─── Milestone Banner ─────────────────────────────────────────────────────────
export function MilestoneBanner({ newMilestones, onDismiss }) {
  const [visible, setVisible] = useState(true);
  if (!visible || !newMilestones || newMilestones.length === 0) return null;
  const m = MILESTONES.find(x => x.id === newMilestones[0]);
  if (!m) return null;

  return (
    <div style={{
      position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
      background: "#1a1a2e", border: "2px solid #4F7FFA", borderRadius: 14,
      padding: "16px 24px", zIndex: 9999, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      display: "flex", alignItems: "center", gap: 16, minWidth: 280, maxWidth: 400,
      animation: "slideUp 0.4s ease",
    }}>
      <div style={{ fontSize: 36 }}>{m.icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: "#4F7FFA", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>
          🏆 Milestone Unlocked!
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", marginBottom: 2 }}>{m.title}</div>
        <div style={{ fontSize: 13, color: "#8899BB" }}>{m.desc}</div>
      </div>
      <button onClick={() => { setVisible(false); onDismiss(); }} style={{
        background: "none", border: "none", color: "#8899BB", fontSize: 18, cursor: "pointer", padding: 4,
      }}>✕</button>
      <style>{`@keyframes slideUp { from { opacity:0; transform: translateX(-50%) translateY(20px); } to { opacity:1; transform: translateX(-50%) translateY(0); } }`}</style>
    </div>
  );
}

// ─── Main Rewards Page ────────────────────────────────────────────────────────
export default function Milestones({ accounts, allPayments, onBack, monthlyPaymentsHistory }) {
  const isMobile = useIsMobile();
  const { earned, streak, totalPrincipal, maxProgress, interestSaved } =
    calculateRewards(accounts, allPayments, monthlyPaymentsHistory);

  const [activeTab, setActiveTab] = useState("all");

  const categories = [
    { id: "all",       label: "All"          },
    { id: "streak",    label: "🔥 Streaks"    },
    { id: "extra",     label: "⚡ Extra"       },
    { id: "paid",      label: "⭐ Paid Off"    },
    { id: "progress",  label: "🌱 Progress"    },
    { id: "reduction", label: "📉 Reduction"   },
    { id: "savings",   label: "💰 Savings"     },
    { id: "strategy",  label: "🎯 Strategy"    },
    { id: "payoff",    label: "🎉 Payoffs"     },
    { id: "endgame",   label: "🏁 End Game"    },
    { id: "journey",   label: "📋 Journey"     },
  ];

  const filtered = activeTab === "all" ? MILESTONES : MILESTONES.filter(m => m.category === activeTab);
  const earnedCount = earned.length;
  const totalCount = MILESTONES.length;
  const pct = Math.round((earnedCount / totalCount) * 100);

  // Next unpearned milestone to show as "next up"
  const nextMilestone = MILESTONES.find(m => !earned.includes(m.id));

  return (
    <div style={{ minHeight: "100vh", background: "#F8F9FC", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#1a1a2e" }}>
      <main style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? "16px 12px" : "32px 24px" }}>
        <button style={s.backBtn} onClick={onBack}>← Back to Dashboard</button>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, marginBottom: 6 }}>🏆 Your Milestones</h1>
          <p style={{ fontSize: 14, color: "#888" }}>Track your progress and celebrate your wins on the road to debt freedom.</p>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, 1fr)", gap: 12, marginBottom: 28 }}>
          <div style={s.statCard}>
            <div style={s.statIcon}>🔥</div>
            <div style={s.statValue}>{streak.current}</div>
            <div style={s.statLabel}>Month Streak</div>
            {streak.longest > streak.current && (
              <div style={s.statSub}>Best: {streak.longest}</div>
            )}
          </div>
          <div style={s.statCard}>
            <div style={s.statIcon}>🏅</div>
            <div style={s.statValue}>{earnedCount}<span style={{ fontSize: 14, color: "#888", fontWeight: 500 }}>/{totalCount}</span></div>
            <div style={s.statLabel}>Milestones</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statIcon}>💰</div>
            <div style={{ ...s.statValue, fontSize: isMobile ? 16 : 20 }}>{formatCurrency(totalPrincipal)}</div>
            <div style={s.statLabel}>Principal Paid</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statIcon}>📉</div>
            <div style={{ ...s.statValue, fontSize: isMobile ? 16 : 20, color: "#10B981" }}>{formatCurrency(interestSaved)}</div>
            <div style={s.statLabel}>Interest Saved</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statIcon}>🎯</div>
            <div style={s.statValue}>{Math.round(maxProgress)}<span style={{ fontSize: 14, fontWeight: 500 }}>%</span></div>
            <div style={s.statLabel}>Best Progress</div>
          </div>
        </div>

        {/* Overall progress bar */}
        <div style={{ ...s.card, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Overall Progress</div>
            <div style={{ fontSize: 13, color: "#4F7FFA", fontWeight: 700 }}>{earnedCount} of {totalCount} milestones earned</div>
          </div>
          <div style={{ background: "#F0F0F5", borderRadius: 8, height: 14, overflow: "hidden", marginBottom: 8 }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #4F7FFA, #A855F7)", borderRadius: 8, transition: "width 0.6s" }} />
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>{pct}% complete</div>
        </div>

        {/* Next up */}
        {nextMilestone && (
          <div style={{ ...s.card, marginBottom: 24, background: "#1a1a2e", border: "none" }}>
            <div style={{ fontSize: 11, color: "#4F7FFA", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Next Milestone</div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ fontSize: 32, filter: "grayscale(1)", opacity: 0.5 }}>{nextMilestone.icon}</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{nextMilestone.title}</div>
                <div style={{ fontSize: 13, color: "#8899BB" }}>{nextMilestone.desc}</div>
              </div>
            </div>
          </div>
        )}

        {/* Category tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setActiveTab(cat.id)} style={{
              ...s.tab,
              ...(activeTab === cat.id ? s.tabActive : {}),
            }}>{cat.label}</button>
          ))}
        </div>

        {/* Milestone grid */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 14 }}>
          {filtered.map(m => {
            const isEarned = earned.includes(m.id);
            return (
              <div key={m.id} style={{
                ...s.milestoneCard,
                ...(isEarned ? s.milestoneEarned : s.milestoneLocked),
              }}>
                <div style={{ fontSize: 36, marginBottom: 10, filter: isEarned ? "none" : "grayscale(1)", opacity: isEarned ? 1 : 0.35 }}>
                  {m.icon}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: isEarned ? "#1a1a2e" : "#aaa" }}>
                  {m.title}
                </div>
                <div style={{ fontSize: 12, color: isEarned ? "#555" : "#bbb", lineHeight: 1.4 }}>
                  {m.desc}
                </div>
                {isEarned && (
                  <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: "#10B981", background: "#D1FAE5", borderRadius: 20, padding: "3px 10px", display: "inline-block" }}>
                    ✓ Earned
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

const s = {
  backBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 14, cursor: "pointer", color: "#555", marginBottom: 24, fontWeight: 500 },
  card: { background: "#fff", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)" },
  statCard: { background: "#fff", borderRadius: 14, padding: "18px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", textAlign: "center" },
  statIcon: { fontSize: 24, marginBottom: 6 },
  statValue: { fontSize: 26, fontWeight: 900, color: "#1a1a2e", lineHeight: 1 },
  statLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 },
  statSub: { fontSize: 11, color: "#aaa", marginTop: 3 },
  tab: { background: "#F3F4F6", border: "1.5px solid transparent", borderRadius: 20, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#555" },
  tabActive: { background: "#EEF2FF", borderColor: "#4F7FFA", color: "#4F7FFA" },
  milestoneCard: { borderRadius: 14, padding: "20px 16px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "transform 0.15s", display: "flex", flexDirection: "column", alignItems: "center" },
  milestoneEarned: { background: "#fff", border: "2px solid #10B981" },
  milestoneLocked: { background: "#F8F9FC", border: "2px solid #E5E7EB" },
};
